import { useQuery } from "@tanstack/react-query";
import * as React from "react";

import { REASON_LABEL } from "@/components/chat/answer-rating";
import { AccuracyChip, JudgeNotes } from "@/components/evaluations/accuracy";
import { asPct } from "@/components/evaluations/charts";
import { Card, Skeleton, selectClass } from "@/components/ui/primitives";
import { EmptyState, ErrorState } from "@/components/ui/states";
import { type RatingFilter, listRatings } from "@/lib/api/chat";
import { cn, formatDate } from "@/lib/utils";
import type { EvaluationKpis } from "@/types/api";

export const PERIODS = [7, 30, 90] as const;

/** A filter select sized to its content, so several sit in one row. */
export const filterSelect = cn(selectClass, "h-9 w-auto");

export function PeriodSelect({
  value,
  onChange,
}: {
  value: number;
  onChange: (d: number) => void;
}) {
  return (
    <select
      aria-label="Period"
      className={filterSelect}
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
    >
      {PERIODS.map((d) => (
        <option key={d} value={d}>
          Last {d} days
        </option>
      ))}
    </select>
  );
}

/** "Oct 12" from an ISO date, without timezone drift. */
export const dayLabel = (iso: string) => {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y!, m! - 1, d).toLocaleDateString("en", { month: "short", day: "numeric" });
};

export function Tile({
  label,
  value,
  sub,
}: {
  label: string;
  value: React.ReactNode;
  sub: React.ReactNode;
}) {
  return (
    <Card className="grid gap-0.5 p-4">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="text-2xl font-semibold tabular-nums">{value}</span>
      <span className="text-xs tabular-nums text-muted-foreground">{sub}</span>
    </Card>
  );
}

function Change({ now, before }: { now: number | null; before: number | null }) {
  if (now == null || before == null) return <>No earlier period to compare</>;
  const pts = Math.round((now - before) * 100);
  if (pts === 0) return <>No change vs previous period</>;
  return (
    <span className={pts > 0 ? "text-[hsl(var(--success))]" : "text-red-500"}>
      {pts > 0 ? "▲" : "▼"} {Math.abs(pts)} pts vs previous period
    </span>
  );
}

export function KpiRow({
  kpis,
  days,
  third,
}: {
  kpis: EvaluationKpis;
  days: number;
  /** The third tile differs between the overview and an agent's page. */
  third: { label: string; value: React.ReactNode; sub: React.ReactNode };
}) {
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
      <Tile
        label={`Approval rate · ${days} days`}
        value={asPct(kpis.approval_rate)}
        sub={<Change now={kpis.approval_rate} before={kpis.previous_approval_rate} />}
      />
      <Tile
        label="Ratings"
        value={(kpis.up + kpis.down).toLocaleString()}
        sub={`👍 ${kpis.up} · 👎 ${kpis.down}`}
      />
      <Tile {...third} />
    </div>
  );
}

/** The rated answers, with the question, the answer and the reason. */
export function RatedAnswers({
  agent,
  days,
  versions,
}: {
  agent: string;
  days: number;
  versions: string[];
}) {
  const [rating, setRating] = React.useState<RatingFilter>("down");
  const [version, setVersion] = React.useState("");
  const q = useQuery({
    queryKey: ["my", "ratings", agent, rating, version, days],
    queryFn: () => listRatings({ rating, agent, version: version || undefined, days }),
  });

  return (
    <Card className="grid gap-3 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold">Rated answers</h3>
        <div className="flex flex-wrap gap-2">
          <select
            aria-label="Rating"
            className={filterSelect}
            value={rating}
            onChange={(e) => setRating(e.target.value as RatingFilter)}
          >
            <option value="down">👎 Thumbs down</option>
            <option value="up">👍 Thumbs up</option>
            <option value="all">All ratings</option>
          </select>
          <select
            aria-label="Version"
            className={filterSelect}
            value={version}
            onChange={(e) => setVersion(e.target.value)}
          >
            <option value="">All versions</option>
            {versions.map((v) => (
              <option key={v} value={v}>
                v{v}
              </option>
            ))}
          </select>
        </div>
      </div>
      {q.isError ? (
        <ErrorState error={q.error} onRetry={() => q.refetch()} />
      ) : q.isLoading ? (
        <Skeleton className="h-32 w-full" />
      ) : !q.data?.items.length ? (
        <EmptyState
          title="No rated answers here"
          hint="Change the filters, or ask an evaluator to rate this agent's answers in the chat app."
        />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="text-left font-mono text-[11px] uppercase tracking-wide text-muted-foreground">
                <th className="px-2 py-2 font-medium">When</th>
                <th className="px-2 py-2 font-medium">Question and answer</th>
                <th className="px-2 py-2 font-medium">Rating</th>
                <th className="px-2 py-2 font-medium">Evaluator's reason</th>
                <th className="px-2 py-2 font-medium">Automatic evaluation</th>
                <th className="px-2 py-2 font-medium">Version</th>
              </tr>
            </thead>
            <tbody>
              {q.data.items.map((r) => (
                <tr key={r.id} className="border-t align-top">
                  <td className="whitespace-nowrap px-2 py-2 text-xs text-muted-foreground">
                    {formatDate(r.rated_at)}
                  </td>
                  <td className="max-w-[420px] px-2 py-2">
                    <p className="font-medium">{r.question ?? "—"}</p>
                    <p className="line-clamp-3 whitespace-pre-wrap text-muted-foreground">
                      {r.answer ?? "—"}
                    </p>
                    {r.run_id ? (
                      <p className="font-mono text-[11px] text-muted-foreground" title={r.run_id}>
                        run {r.run_id.slice(0, 8)}
                      </p>
                    ) : null}
                  </td>
                  <td className="whitespace-nowrap px-2 py-2">
                    <span
                      className={
                        r.rating === "up"
                          ? "font-semibold text-[hsl(var(--success))]"
                          : "font-semibold text-red-500"
                      }
                    >
                      {r.rating === "up" ? "👍 Good" : "👎 Bad"}
                    </span>
                  </td>
                  <td className="px-2 py-2">
                    {r.category ? (
                      <span className="rounded border px-1.5 text-[11px] text-muted-foreground">
                        {REASON_LABEL[r.category]}
                      </span>
                    ) : null}
                    {r.reason ? <p className="whitespace-pre-wrap">{r.reason}</p> : null}
                    <p className="text-[11px] text-muted-foreground">{r.rater_email}</p>
                  </td>
                  <td className="max-w-[360px] px-2 py-2">
                    {r.automatic ? (
                      <div className="grid gap-1">
                        <AccuracyChip
                          level={r.automatic.overall}
                          pct={r.automatic.accuracy_pct}
                          className="w-fit"
                        />
                        <JudgeNotes
                          explanation={r.automatic.explanation}
                          fix={r.automatic.suggested_fix}
                        />
                      </div>
                    ) : (
                      <p className="text-xs text-muted-foreground">
                        Not scored: no tool or knowledge-base data to check it against.
                      </p>
                    )}
                  </td>
                  <td className="px-2 py-2 font-mono text-xs">v{r.agent_version}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
